import React, {useCallback, useEffect, useMemo, useRef, useState,} from "react";
import ReactFlow, {
  addEdge,
  Background,
  Controls,
  MiniMap,
  Panel,
  ReactFlowProvider,
  useEdgesState,
  useNodesState,
  useReactFlow,
} from "reactflow";
import {BrowserRouter, useSearchParams} from 'react-router-dom';
import "reactflow/dist/base.css";
import Sidebar from "./components/Sidebar.jsx";
import PromptNode from "./components/PromptNode.jsx";
import ToolNode from "./components/ToolNode.jsx";
import IntentNode from "./components/IntentNode.jsx";
import NumberNode from "./components/NumberNode.jsx";
import LeftSidebar from "./components/LeftSidebar.jsx";
import {LanguageProvider} from './i18n/LanguageContext';
import {useTranslation} from './i18n/useTranslation';
import {GlobalStateProvider} from './i18n/GlobalStateContext';
import {initGlobalInputProtection} from './services/GlobalInputProtection';
import {containsDangerousUnicode} from './utils/inputSanitizer';
import {validatePhoneNumber} from './utils/phoneFormatter';

// Key for local storage
const flowKey = "flow-key";

// Add this near the top of the file, after imports
const API_BASE_URL = process.env.REACT_APP_API_URL || '';

// Function for generating unique IDs for nodes
let id = 3;
const getId = () => `node_${id++}`;
let prompts = [];

const FlowWithProvider = () => {
  const reactFlowWrapper = useRef(null);
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [reactFlowInstance, setReactFlowInstance] = useState(null);
  const [selectedElements, setSelectedElements] = useState([]);
  const [nodeText, setNodeText] = useState("");
  const [nodePrompt, setNodePrompt] = useState("");
  const [nodePromptInitialMessage, setNodePromptInitialMessage] = useState("");
  const [nodePromptSystemInstruction, setNodePromptSystemInstruction] = useState("");
  const [nodeTool, setNodeTool] = useState("");
  const [nodeToolDescription, setNodeToolDescription] = useState("");
  const [nodeIntent, setNodeIntent] = useState("");
  const [nodeIntentDescription, setNodeIntentDescription] = useState("");
  const [nodeIntentSpeak, setNodeIntentSpeak] = useState("");
  const [nodeIntentForwardPhone, setNodeIntentForwardPhone] = useState("");
  const [isPanelVisible, setIsPanelVisible] = useState(true);
  const [nodeLinkto, setNodeLinkto] = useState("");
  const [nodeAgentName, setNodeAgentName] = useState("");
  const [nodeMuteUser, setNodeMuteUser] = useState(false);
  const [searchParams] = useSearchParams();
  const t = useTranslation();


  // Helper function to validate intent names
  const validateIntentName = useCallback((intentName) => {
    // Check for special characters that could cause issues
    // Allow: letters, numbers, spaces, hyphens, underscores
    // Disallow: quotes, backslashes, and other special characters
    const invalidCharRegex = /['"\\<>{}[\]|`~!@#$%^&*()+=;:,?/]/;
    return !invalidCharRegex.test(intentName);
  }, []);

  // Helper function to show a warning message
  const showWarning = useCallback((translationKey, customMessage = null) => {
    // Create a warning message element
    const warningDiv = document.createElement('div');
    warningDiv.className = 'fixed top-1/3 left-1/2 transform -translate-x-1/2 bg-yellow-100 border-l-4 border-yellow-500 text-yellow-700 p-4 rounded shadow-lg z-50';
    warningDiv.style.maxWidth = '400px';
    
    const warningTitle = document.createElement('h3');
    warningTitle.className = 'font-bold mb-2';
    warningTitle.innerText = t('warning');
    
    const warningMessage = document.createElement('p');
    warningMessage.innerText = customMessage || t(translationKey);
    
    const closeButton = document.createElement('button');
    closeButton.className = 'mt-3 bg-yellow-500 hover:bg-yellow-600 text-white font-bold py-1 px-3 rounded text-sm';
    closeButton.innerText = 'OK';
    closeButton.onclick = () => {
      document.body.removeChild(warningDiv);
    };
    
    warningDiv.appendChild(warningTitle);
    warningDiv.appendChild(warningMessage);
    warningDiv.appendChild(closeButton);
    
    // Add to body
    document.body.appendChild(warningDiv);
    
    // Auto-remove after 5 seconds
    setTimeout(() => {
      if (document.body.contains(warningDiv)) {
        document.body.removeChild(warningDiv);
      }
    }, 5000);
  }, [t]);

  // Define custom node types
  const nodeTypes = useMemo(
    () => ({
      promptnode: PromptNode,
      toolnode: ToolNode,
      intentnode: IntentNode,
      numbernode: NumberNode,
    }),
    []
  );
  useEffect(() => {
    const fetchNodes = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}?${searchParams}`, {
          method: 'GET'
        });
        const data = await response.json();

        let startX = 0;
        let startY = 100;
        let y_spacing = 300; // Vertical spacing between levels
        let x_spacing = 250; // Horizontal spacing between nodes at the same level
        let node_width = 200;
        let nodes = [];
        let new_edges = [];
        
        // Track the maximum Y position for each level
        let maxYByLevel = {};
        // Track the next X position for each level
        let nextXByLevel = {};
        // Track created function nodes to avoid duplicates
        let functionNodes = {};

        // store prompts in an array
        if (data.general != null) {
          prompts = data.general.prompts;
        }

        if (data.number != null) {
          let new_node;
          new_node = {
            id: `number_${data.number}`,
            type: 'numbernode',
            data: {
              phone_number: data.number,
              link_to: data.link_to,
              voice_agent_name: data.voice_agent_name || ""
            },
            position: {
              x: startX,
              y: 100
            }
          }
          nodes.push(new_node);
          maxYByLevel[0] = 100 + 80; // Initial height + node height
          nextXByLevel[0] = startX + x_spacing;
        }

        // Recursive function to process prompts and their nested prompts
        const processPromptNode = (prompt, parentId = null, level = 0, parentX = null) => {

          // Calculate Y position based on the maximum Y of the previous level
          const baseY = maxYByLevel[level - 1] || startY;
          const yPos = baseY + y_spacing;
          
          // For X position, if this is a nested prompt (has a parent intent),
          // use the same X position as the parent intent
          let xPos;
          if (parentId && parentX !== null) {
            // Align with parent intent
            xPos = parentX;
          } else {
            // Regular positioning for root prompts or first level
            if (nextXByLevel[level] === undefined) {
              nextXByLevel[level] = startX;
            }
            xPos = nextXByLevel[level];
            nextXByLevel[level] = xPos + x_spacing;
          }
          
          // Update the maximum Y position for this level
          maxYByLevel[level] = Math.max(maxYByLevel[level] || 0, yPos + 150); // Add node height
          
          const promptNodeId = `prompt_${prompt.id}`;
          
          // Create prompt node
          const promptNode = {
            id: promptNodeId,
            type: 'promptnode',
            data: {
              text: prompt.name,
              prompt: prompt.prompt,
              promptInitialMessage: prompt.initial_response || prompt.initial_announcement || "",
              promptSystemInstruction: prompt.system || "",
              promptId: String(prompt.id), // Store promptId as string in the node data
              muteUser: prompt.mute_user // Initialize mute state
            },
            position: {
              x: xPos,
              y: yPos
            }
          };
          
          nodes.push(promptNode);
          
          // Create edge from parent to this prompt if parent exists
          if (parentId) {
            const edge = {
              id: `edge_${parentId}_to_${promptNodeId}`,
              sourceHandle: 'c',
              targetHandle: 'a',
              source: parentId,
              target: promptNodeId
            };
            new_edges.push(edge);
          }
          
          // Process tools
          if (prompt.tools && prompt.tools.length > 0) {

            // Ensure function nodes start clearly below the prompt node
            const promptHeight = 150; // Estimated height of the prompt node
            const functionStartX = xPos + node_width + 50; // Position to the right of the prompt
            const functionStartY = yPos + promptHeight + 50; // Start well below the prompt
            const functionYSpacing = 120; // Vertical spacing between function nodes
            let maxFunctionBottom = 0; // Track the bottom of the last function node
            
            prompt.tools.forEach((toolItem, toolIndex) => {
              // Check if this function has already been created
              const functionName = toolItem.name;
              let toolNodeId;
              let toolNodePosition;
              
              if (functionNodes[functionName]) {
                // Reuse existing function node
                toolNodeId = functionNodes[functionName];
                // Find the position of the existing tool node
                const existingNode = nodes.find(node => node.id === toolNodeId);
                if (existingNode) {
                }
              } else {
                // Create new function node
                toolNodeId = `tool_${toolItem.id}`;
                functionNodes[functionName] = toolNodeId;
                
                // Position function nodes in a vertical column, starting below the prompt
                toolNodePosition = {
                  x: functionStartX,
                  y: functionStartY + (toolIndex * functionYSpacing)
                };
                
                let toolNode = {
                  id: toolNodeId,
                  type: 'toolnode',
                  data: {
                    tool: toolItem.name,
                    toolDescription: toolItem.description
                  },
                  position: toolNodePosition
                };
                
                nodes.push(toolNode);
                
                // Update the max function bottom position
                const functionNodeBottom = toolNodePosition.y + 80; // Add node height
                maxFunctionBottom = Math.max(maxFunctionBottom, functionNodeBottom);
              }
              
              // Create edge from prompt to function node
              let toolEdge = {
                id: `edge_tool_${promptNode.id}_${toolNodeId}_${toolIndex}`,
                sourceHandle: 'c',
                targetHandle: 'a',
                source: promptNode.id,
                target: toolNodeId
              };
              
              new_edges.push(toolEdge);
            });
            
            // Update the maximum Y position to ensure intent nodes are placed below all function nodes
            // Add extra spacing below the function nodes
            maxYByLevel[level] = Math.max(maxYByLevel[level] || 0, maxFunctionBottom + 120);
          }
          
          // Process intents
          let intentLevel = level + 1;
          if (prompt.intents && prompt.intents.length > 0) {

            // Calculate the Y position for intents - ensure they're below any function nodes
            const intentBaseY = maxYByLevel[level] + 50; // Add padding below function nodes
            
            // Calculate total width needed for intents
            const totalWidth = prompt.intents.length * x_spacing;
            // Calculate starting X position to center intents under the prompt
            const startingX = xPos - (totalWidth / 2) + (node_width / 2);
            
            // Process each intent
            prompt.intents.forEach((intent, intentIndex) => {

              // Calculate position for this intent - align in a row centered under the prompt
              const intentXPos = startingX + (intentIndex * x_spacing);
              const intentNodeId = `intent_${intent.id}`;
              
              let intentNode = {
                id: intentNodeId,
                type: 'intentnode',
                data: {
                  intent: intent.name,
                  intentDescription: intent.description,
                  intentSpeak: intent.say,
                  intentForwardPhone: intent.forward_number
                },
                position: {
                  x: intentXPos,
                  y: intentBaseY,
                }
              };
              
              nodes.push(intentNode);
              
              // Update the maximum Y position for the intent level
              const intentHeight = 120; // Approximate height of intent node
              maxYByLevel[intentLevel] = Math.max(maxYByLevel[intentLevel] || 0, intentBaseY + intentHeight);
              
              let intentEdge = {
                id: `edge_intent_${promptNode.id}_${intentNodeId}`,
                sourceHandle: 'b',
                targetHandle: 'a',
                source: promptNode.id,
                target: intentNodeId
              };
              
              new_edges.push(intentEdge);
              
              // Process nested prompt if it exists
              if (intent.nested_prompt) {
                // Process nested prompt directly under this intent
                processPromptNode(intent.nested_prompt, intentNodeId, intentLevel + 1, intentXPos);
              }
            });
          }
          
          return promptNodeId;
        };

        // Process all root nodes
        data.nodes.forEach((node) => {
          processPromptNode(node, null, 0);
        });

        // After all nodes and edges are created, check for intent-prompt connections
        // and update the hasConnectedPrompt flag accordingly
        nodes.forEach(node => {
          if (node.type === 'intentnode') {
            // Check if this intent node has a connection to a prompt node
            const connectedToPrompt = new_edges.some(edge => 
              edge.source === node.id && 
              (edge.target.startsWith('prompt_') || 
               nodes.some(n => n.id === edge.target && n.type === 'promptnode'))
            );
            
            if (connectedToPrompt) {
              // Update the node data to indicate it has a connected prompt
              node.data.hasConnectedPrompt = true;
            }
          }
        });

        if (data.number != null) {
          // Find the first prompt node
          const firstPromptNode = nodes.find(node => node.type === 'promptnode');
          if (firstPromptNode) {
            // Update the number node's X position to match the first prompt
            const numberNode = nodes.find(node => node.type === 'numbernode');
            if (numberNode) {
              numberNode.position.x = firstPromptNode.position.x;
            }
          }
        }

        // Connect number node to first prompt if link_to is specified
        if (data.link_to != null && data.link_to !== "") {
          let new_edge = {
            id: `edge_number`,
            sourceHandle: 'a',
            targetHandle: 'a',
            source: `number_${data.number}`,
            target: `prompt_` + data.link_to,
          }
          new_edges.push(new_edge);
        }
        
        setNodes(nodes);
        setEdges(new_edges);
      } catch (error) {
        console.error('Error loading JSON:', error);
        return {nodes: [], edges: []};
      }
    };
    fetchNodes();
  }, [searchParams, setEdges, setNodes]);


  // Effect to update node data when inputs change
  useEffect(() => {
    if (selectedElements.length > 0) {
      setNodes((nodes) =>
        nodes.map((node) => {
          if (node.id === selectedElements[0].id) {
            if (selectedElements[0].type === "numbernode") {
              return {
                ...node,
                data: {
                  ...node.data,
                  phone_number: nodeText,
                  link_to: nodeLinkto,
                  voice_agent_name: nodeAgentName
                },
              };
            } else if (selectedElements[0].type === "promptnode") {
              return {
                ...node,
                data: {
                  ...node.data,
                  text: nodeText,
                  prompt: nodePrompt,
                  promptInitialMessage: nodePromptInitialMessage,
                  promptSystemInstruction: nodePromptSystemInstruction,
                  muteUser: nodeMuteUser
                },
              };
            } else if (selectedElements[0].type === "toolnode") {
              return {
                ...node,
                data: {
                  ...node.data,
                  tool: nodeTool,
                  toolDescription: nodeToolDescription
                },
              };
            } else if (selectedElements[0].type === "intentnode") {
              return {
                ...node,
                data: {
                  ...node.data,
                  intent: nodeIntent,
                  intentDescription: nodeIntentDescription,
                  intentSpeak: nodeIntentSpeak,
                  intentForwardPhone: nodeIntentForwardPhone
                },
              };
            }
          }
          return node;
        })
      );
    } else {
      setNodeText(""); // Clear nodeText when no node is selected
      setNodePrompt(""); // Clear nodePrompt when no node is selected
      setNodePromptInitialMessage(""); // Clear nodePromptInitialMessage when no node is selected
      setNodePromptSystemInstruction(""); // Clear nodePromptSystemInstruction when no node is selected
      setNodeTool(""); // Clear nodeImg when no node is selected
      setNodeToolDescription(""); // Clear nodeToolDescription when no node is selected
      setNodeIntent(""); // Clear nodeColor when no node is selected
      setNodeIntentDescription(""); // Clear nodeIntentDescription when no node is selected
      setNodeIntentSpeak(""); // Clear nodeIntentSpeak when no node is selected
      setNodeIntentForwardPhone(""); // Clear nodeIntentForwardPhone when no node is selected
      setNodeLinkto(""); // Clear nodeLinkTo when no node is selected
      setNodeAgentName(""); // Clear nodeAgentName when no node is selected
      setNodeMuteUser(false); // Clear nodeMuteUser when no node is selected
    }
      }, [selectedElements, setNodes, nodeText, nodeLinkto, nodePrompt, nodePromptInitialMessage, nodePromptSystemInstruction, nodeTool, nodeToolDescription, nodeIntent, nodeIntentDescription, nodeIntentSpeak, nodeIntentForwardPhone, nodeAgentName, nodeMuteUser]);

  // Handle node click
  const onNodeClick = useCallback(
    (event, node) => {
      setSelectedElements([node]);
      if (node.type === "numbernode") {
        setNodeText(node.data.phone_number);
        setNodeLinkto(node.data.link_to);
        setNodeAgentName(node.data.voice_agent_name || "");
      } else if (node.type === "promptnode") {
        setNodeText(node.data.text);
        setNodePrompt(node.data.prompt);
        setNodePromptInitialMessage(node.data.promptInitialMessage);
        setNodePromptSystemInstruction(node.data.promptSystemInstruction || "");
        setNodeMuteUser(node.data.muteUser || false);
      } else if (node.type === "toolnode") {
        setNodeTool(node.data.tool);
        setNodeToolDescription(node.data.toolDescription);
      } else if (node.type === "intentnode") {
        setNodeIntent(node.data.intent);
        setNodeIntentDescription(node.data.intentDescription);
        setNodeIntentSpeak(node.data.intentSpeak);
        setNodeIntentForwardPhone(node.data.intentForwardPhone);
        
        // Check if this intent node has a connection to a prompt node
        // and update the hasConnectedPrompt flag if needed
        const hasConnectedPrompt = edges.some(edge => 
          edge.source === node.id && 
          (edge.target.startsWith('prompt_') || 
           nodes.some(n => n.id === edge.target && n.type === 'promptnode'))
        );
        
        // If the connection state changed, update the node
        if (hasConnectedPrompt !== !!node.data.hasConnectedPrompt) {
          setNodes(nodes => 
            nodes.map(n => {
              if (n.id === node.id) {
                return {
                  ...n,
                  data: {
                    ...n.data,
                    hasConnectedPrompt
                  }
                };
              }
              return n;
            })
          );
        }
      }
      setNodes((nodes) =>
        nodes.map((n) => ({
          ...n,
          selected: n.id === node.id,
        }))
      );
    },
    [setNodes, edges, nodes]
  );

  // Setup viewport
  const {setViewport} = useReactFlow();

  // Check if any node is unconnected
  const isNodeUnconnected = useCallback(() => {
    let unconnectedNodes = nodes.filter(
      (node) =>
        !edges.find(
          (edge) => edge.source === node.id || edge.target === node.id
        )
    );
    return unconnectedNodes.length > 0;
  }, [nodes, edges]);

  // Toggle mute for all prompt nodes
  const toggleMuteAllPrompts = useCallback(() => {
    setNodes((currentNodes) => {
      // Check if any prompt nodes are currently muted
      const promptNodes = currentNodes.filter(node => node.type === 'promptnode');
      const anyMuted = promptNodes.some(node => node.data.muteUser);
      
      // If any are muted, unmute all. If none are muted, mute all.
      const newMuteState = !anyMuted;
      
      return currentNodes.map(node => {
        if (node.type === 'promptnode') {
          return {
            ...node,
            data: {
              ...node.data,
              muteUser: newMuteState
            }
          };
        }
        return node;
      });
    });
    
    // If the currently selected element is a prompt node, update the mute state in sidebar
    if (selectedElements.length > 0 && selectedElements[0].type === 'promptnode') {
      const promptNodes = nodes.filter(node => node.type === 'promptnode');
      const anyMuted = promptNodes.some(node => node.data.muteUser);
      setNodeMuteUser(!anyMuted);
    }
  }, [setNodes, selectedElements, nodes, setNodeMuteUser]);

  // Save flow to local storage
  const onSave = useCallback(async () => {
    if (reactFlowInstance) {
      const flow = reactFlowInstance.toObject();
      
      // Validate all intent names before saving
      const intentNodes = flow.nodes.filter(node => node.type === 'intentnode');
      for (const intentNode of intentNodes) {
        const intentName = intentNode.data.intent || '';
        if (intentName.trim() && !validateIntentName(intentName)) {
          const errorMessage = `${t('intentNameValidationError')}\n\n${t('intentNode.title')}: "${intentName}"`;
          showWarning('intentNameValidationError', errorMessage);
          return; // Stop the save process
        }
      }

      // Check for unconnected nodes
      if (nodes.length > 1 && isNodeUnconnected()) {
        showWarning('unconnectedNodesWarning');
        return; // Stop the save process
      }
      
      // Check for ASCII control characters in intent nodes only
      // This prevents "invalid ASCII control character in string" errors during JSON serialization
      let intentWithControlChars = null;
      
      for (const intentNode of intentNodes) {
        const fields = [
          { value: intentNode.data.intent, name: 'Intent name' },
          { value: intentNode.data.intentDescription, name: 'Intent description' },
          { value: intentNode.data.intentSpeak, name: 'Speak text' },
          { value: intentNode.data.intentForwardPhone, name: 'Forward phone' }
        ];
        
        for (const field of fields) {
          if (field.value && containsDangerousUnicode(field.value)) {
            intentWithControlChars = {
              intentName: intentNode.data.intent || 'Unnamed Intent',
              fieldName: field.name
            };
            break;
          }
        }
        
        if (intentWithControlChars) break;
      }
      
      if (intentWithControlChars) {
        const errorMessage = `${t('controlCharacterWarning')}\n\n${t('intentNode.title')}: "${intentWithControlChars.intentName}"\n${t('field')}: ${intentWithControlChars.fieldName}`;
        showWarning('controlCharacterWarning', errorMessage);
        return; // Stop the save process
      }
      
      // Validate phone numbers in intent nodes
      let intentWithInvalidPhone = null;
      
      for (const intentNode of intentNodes) {
        const forwardPhone = intentNode.data.intentForwardPhone;
        if (forwardPhone && forwardPhone.trim() !== '') {
          const validation = validatePhoneNumber(forwardPhone);
          if (!validation.valid) {
            intentWithInvalidPhone = {
              intentName: intentNode.data.intent || 'Unnamed Intent',
              phone: forwardPhone,
              errorKey: validation.errorKey
            };
            break;
          }
        }
      }
      
      if (intentWithInvalidPhone) {
        const errorMessage = `${t(intentWithInvalidPhone.errorKey)}\n\n${t('intentNode.title')}: "${intentWithInvalidPhone.intentName}"\n${t('intentNode.forwardPhone')}: "${intentWithInvalidPhone.phone}"`;
        showWarning('phoneValidationError', errorMessage);
        return; // Stop the save process
      }
      
      // Validate that speak field is filled for intent nodes not connected to a prompt
      let intentWithMissingSpeak = null;
      
      for (const intentNode of intentNodes) {
        const hasConnectedPrompt = intentNode.data.hasConnectedPrompt || false;
        const speakField = intentNode.data.intentSpeak;
        
        // If intent is not connected to a prompt, speak field is required
        if (!hasConnectedPrompt && (!speakField || speakField.trim() === '')) {
          intentWithMissingSpeak = {
            intentName: intentNode.data.intent || 'Unnamed Intent'
          };
          break;
        }
      }
      
      if (intentWithMissingSpeak) {
        const errorMessage = `${t('speakFieldRequired')}\n\n${t('intentNode.title')}: "${intentWithMissingSpeak.intentName}"`;
        showWarning('speakFieldRequired', errorMessage);
        return; // Stop the save process
      }
      
      // show a window in the center of the screen with a loading message
      let dOverlay = '<div class="save_div"><p>' + t('save') + '</p></div>';
      document.body.innerHTML += dOverlay;
      await new Promise(resolve => setTimeout(resolve, 1000));
      // create a json file with the flow
      let data = JSON.stringify(flow);
      try {
        const response = await fetch(`${API_BASE_URL}?${searchParams}`, {
          method: 'POST',
          mode: 'no-cors',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
          },
          credentials: 'include',
          body: data,
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
      } catch (error) {
        console.error('Error:', error);
      }


      window.location.reload();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isNodeUnconnected, nodes.length, reactFlowInstance, searchParams, validateIntentName, t, showWarning]);

  // Restore flow from local storage
  const onRestore = useCallback(() => {
    const restoreFlow = async () => {
      const flow = JSON.parse(localStorage.getItem(flowKey));
      if (flow) {
        const {x = 0, y = 0, zoom = 1} = flow.viewport;
        setNodes(flow.nodes || []);
        setEdges(flow.edges || []);
        setViewport({x, y, zoom});
      }
    };
    restoreFlow();
  }, [setNodes, setViewport, setEdges]);

  // Handle edge connection
  const onConnect = useCallback(
    (params) => {
      const {source, target} = params;
      
      // Find the source and target nodes
      const sourceNode = nodes.find(n => n.id === source);
      const targetNode = nodes.find(n => n.id === target);
      
      // Check for invalid connections between same node types
      if (sourceNode && targetNode && sourceNode.type === targetNode.type) {
        // Display warning based on node type
        let warningKey = '';
        
        switch(sourceNode.type) {
          case 'promptnode':
            warningKey = 'promptToPromptWarning';
            break;
          case 'intentnode':
            warningKey = 'intentToIntentWarning';
            break;
          case 'toolnode':
            warningKey = 'functionToFunctionWarning';
            break;
          default:
            warningKey = 'sameTypeConnectionWarning';
        }
        
        showWarning(warningKey);
        return; // Don't create the connection
      }
      
      // If this is a connection from a phone number to a prompt
      if ((sourceNode?.type === "numbernode" || source.startsWith('number_')) && 
          (targetNode?.type === "promptnode" || target.startsWith('prompt_'))) {
        
        // Check if the phone number already has a connection to a prompt node
        const existingPromptConnection = edges.some(edge => 
          (edge.source === source || 
           (sourceNode?.id && edge.source === sourceNode.id)) && 
          (nodes.find(n => n.id === edge.target)?.type === "promptnode" || 
           edge.target.startsWith('prompt_'))
        );
        
        if (existingPromptConnection) {
          showWarning('maxPhonePromptConnectionWarning');
          return; // Don't create the connection
        }
      }
      
      // If this is a connection from an intent to a prompt
      if (sourceNode?.type === "intentnode" && 
          (targetNode?.type === "promptnode" || target.startsWith('prompt_'))) {
        
        // Check if the intent already has a connection to a prompt node
        const existingPromptConnection = edges.some(edge => 
          edge.source === source && 
          (nodes.find(n => n.id === edge.target)?.type === "promptnode" || 
           edge.target.startsWith('prompt_'))
        );
        
        if (existingPromptConnection) {
          showWarning('maxPromptConnectionWarning');
          return; // Don't create the connection
        }
        
        // Update the intent node to reflect the connected state
        setNodes(nodes => 
          nodes.map(node => {
            if (node.id === source) {
              return {
                ...node,
                data: {
                  ...node.data,
                  hasConnectedPrompt: true
                }
              };
            }
            return node;
          })
        );
        
        // Check if the intent node is currently selected - if so, ensure it stays selected
        if (selectedElements.length > 0 && selectedElements[0].id === source) {
          // Force a re-selection of the node to update the sidebar
          setTimeout(() => {
            // Create a fresh copy of the selected element to force a re-render
            const freshSourceNode = nodes.find(n => n.id === source);
            if (freshSourceNode) {
              setSelectedElements([{...freshSourceNode, data: {...freshSourceNode.data, hasConnectedPrompt: true}}]);
            }
          }, 50); // Small delay to ensure edge is added first
        }
      }
      
      // Create the edge
      setEdges((eds) => addEdge(params, eds));
    },
    [setEdges, nodes, selectedElements, setSelectedElements, setNodes, edges, showWarning]
  );

  // Enable drop effect on drag over
  const onDragOver = useCallback((event) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = "move";
  }, []);

  // Handle drop event to add a new node
  const onDrop = useCallback(
    (event) => {
      event.preventDefault();

      const reactFlowBounds = reactFlowWrapper.current.getBoundingClientRect();
      const type = event.dataTransfer.getData("application/reactflow");
      
      // Check if this is an existing prompt being dragged
      let promptData = null;
      const promptDataString = event.dataTransfer.getData("application/prompt-data");
      if (promptDataString) {
        try {
          promptData = JSON.parse(promptDataString);
        } catch (error) {
          console.error("Error parsing prompt data:", error);
        }
      }
      
      // only use dropped element if it has a valid type
      if (typeof type === "undefined" || !type) {
        return;
      }

      const position = reactFlowInstance.project({
        x: event.clientX - reactFlowBounds.left,
        y: event.clientY - reactFlowBounds.top,
      });

      // Handle existing prompt nodes differently
      if (type === "existingpromptnode" && promptData) {
        try {

          // First, create a basic node with available information
          const promptNodeId = getId();
          const newNode = {
            id: promptNodeId,
            type: "promptnode",
            position,
            data: {
              text: promptData.promptName || "Imported Prompt",
              promptId: String(promptData.promptId), // Ensure promptId is stored as string
              prompt: "",
              promptInitialMessage: "",
              promptSystemInstruction: "",
              hasConnectedPrompt: false,
              tools: []
            },
          };
          
          // Add the node to the canvas
          setNodes((nds) => nds.concat(newNode));
          
          // Then fetch complete prompt data to update the node
          fetch(`${API_BASE_URL}?${searchParams}&prompt_id=${promptData.promptId}`)
            .then(response => response.json())
            .then(data => {

              if (data && data.nodes) {
                // Find the prompt node in the response
                const promptNode = data.nodes.find(node => 
                  node.id.toString() === promptData.promptId.toString()
                );
                
                if (promptNode) {

                  // Update the existing prompt node with complete data
                  setNodes(nds => 
                    nds.map(node => {
                      if (node.id === promptNodeId) {
                        return {
                          ...node,
                          data: {
                            ...node.data,
                            text: promptNode.name || promptData.promptName,
                            prompt: promptNode.prompt || "",
                            promptInitialMessage: promptNode.initial_response || promptNode.initial_announcement || "",
                            promptSystemInstruction: promptNode.system_instruction || "",
                            tools: promptNode.tools || [],
                            promptId: String(promptData.promptId) // Ensure promptId is still there and as string
                          }
                        };
                      }
                      return node;
                    })
                  );
                  
                  // Process functions/tools if they exist
                  if (promptNode.tools && promptNode.tools.length > 0) {
                    // Track new nodes and edges to add
                    let newNodes = [];
                    let newEdges = [];
                    
                    promptNode.tools.forEach((tool, index) => {
                      const toolNodeId = `${promptNodeId}_tool_${index}`;
                      
                      // Create tool node
                      const toolNode = {
                        id: toolNodeId,
                        type: 'toolnode',
                        position: {
                          x: position.x + 250,
                          y: position.y + (index * 100)
                        },
                        data: {
                          text: tool.name || `Tool ${index + 1}`,
                          tool: tool.name || "",
                          toolDescription: tool.description || ""
                        }
                      };
                      
                      // Add to our collection of new nodes
                      newNodes.push(toolNode);
                      
                      // Create edge from prompt to function node
                      const toolEdge = {
                        id: `edge_tool_${promptNodeId}_${toolNodeId}`,
                        sourceHandle: 'c',
                        targetHandle: 'a',
                        source: promptNodeId,
                        target: toolNodeId
                      };
                      
                      // Add to our collection of new edges
                      newEdges.push(toolEdge);
                    });
                    
                    // Add all new nodes and edges to the canvas
                    if (newNodes.length > 0) {
                      setNodes(nds => [...nds, ...newNodes]);
                    }
                    
                    if (newEdges.length > 0) {
                      setEdges(eds => [...eds, ...newEdges]);
                    }
                  }
                }
              }
            })
            .catch(error => {
              console.error("Error fetching prompt data:", error);
            });
        } catch (error) {
          console.error("Error handling prompt drop:", error);
        }
      } else {
        // Regular node creation for new nodes
        const newNode = {
          id: getId(),
          type,
          position,
          data: {
            text: type === "promptnode" ? "New prompt" : undefined,
            tool: type === "toolnode" ? "the tool" : undefined,
            intent: type === "intentnode" ? "the intent" : undefined,
            hasConnectedPrompt: false, // Initialize to false for all nodes
            muteUser: type === "promptnode" ? false : undefined, // Initialize mute state only for prompt nodes
          },
        };
        
        setNodes((nds) => nds.concat(newNode));
      }
    },
    [reactFlowInstance, setNodes, setEdges, searchParams]
  );

  // Custom handler for edge changes
  const handleEdgesChange = useCallback((changes) => {
    // Check for edge removals
    changes.forEach(change => {
      if (change.type === 'remove') {
        // Find the edge that's being removed
        const removedEdge = edges.find(edge => edge.id === change.id);
        if (removedEdge) {
          // Find the source node (could be an intent node)
          const sourceNode = nodes.find(node => node.id === removedEdge.source);
          // Find the target node (could be a prompt node)
          const targetNode = nodes.find(node => node.id === removedEdge.target);
          
          // If we're removing a connection from an intent to a prompt
          if (sourceNode?.type === 'intentnode' && 
              (targetNode?.type === 'promptnode' || removedEdge.target.startsWith('prompt_'))) {
            
            // Update the intent node to reflect the disconnected state
            setNodes(nodes => 
              nodes.map(node => {
                if (node.id === sourceNode.id) {
                  return {
                    ...node,
                    data: {
                      ...node.data,
                      hasConnectedPrompt: false
                    }
                  };
                }
                return node;
              })
            );
            
            // If the intent node is currently selected, also update the selectedElements
            if (selectedElements.length > 0 && selectedElements[0].id === sourceNode.id) {
              setTimeout(() => {
                // Create a fresh copy of the selected element to force a re-render
                const freshSourceNode = nodes.find(n => n.id === sourceNode.id);
                if (freshSourceNode) {
                  setSelectedElements([{...freshSourceNode, data: {...freshSourceNode.data, hasConnectedPrompt: false}}]);
                }
              }, 50); // Small delay to ensure edge is removed first
            }
          }
        }
      }
    });
    
    // Apply the edge changes
    onEdgesChange(changes);
  }, [onEdgesChange, edges, nodes, selectedElements, setSelectedElements, setNodes]);

  const handleNodesChange = useCallback(
    (changes) => {
      // Check for node deletions
      const deletions = changes.filter(change => change.type === 'remove');
      
      if (deletions.length > 0) {
        // Collect all nodes that need to be deleted (original + descendants)
        const nodesToDelete = new Set();
        
        // Function to check if a node has parents other than the specified one
        const hasOtherParents = (nodeId, excludeParentId) => {
          return edges.some(edge => 
            edge.target === nodeId && 
            edge.source !== excludeParentId
          );
        };
        
        // Function to recursively find all descendants of a node
        const findDescendants = (nodeId, parentPath = new Set()) => {
          // Prevent infinite recursion in case of cycles
          if (parentPath.has(nodeId)) {
            return;
          }
          
          // Add this node to the parent path for cycle detection
          const newParentPath = new Set(parentPath);
          newParentPath.add(nodeId);
          
          // Find all edges where this node is the source
          const outgoingEdges = edges.filter(edge => edge.source === nodeId);
          
          // For each outgoing edge, add the target node and its descendants to nodesToDelete
          outgoingEdges.forEach(edge => {
            const targetNodeId = edge.target;
            
            // Check if the target node has other parents
            if (!hasOtherParents(targetNodeId, nodeId)) {
              // If no other parents, it's safe to delete this node and its descendants
              nodesToDelete.add(targetNodeId);
              
              // Recursively find and add all descendants of the target node
              findDescendants(targetNodeId, newParentPath);
            } else {
              // We still need to delete the edge to this node
              // This will be handled later when we filter edges
            }
          });
        };
        
        // Process each deletion
        deletions.forEach(deletion => {
          const deletedNode = nodes.find(node => node.id === deletion.id);
          
          if (deletedNode) {
            // Find all descendants of this node and mark them for deletion
            findDescendants(deletedNode.id);
          }
        });
        
        // Create additional node deletion changes for all descendants
        const additionalDeletions = [...nodesToDelete].map(nodeId => ({
          type: 'remove',
          id: nodeId
        }));
        
        // If we found additional nodes to delete, add them to the changes
        if (additionalDeletions.length > 0) {
          changes = [...changes, ...additionalDeletions];
          
          // Also need to delete all edges connected to deleted nodes
          const edgesToDelete = edges.filter(edge => 
            nodesToDelete.has(edge.source) || 
            (nodesToDelete.has(edge.target) && !hasOtherParents(edge.target, edge.source))
          );
          
          if (edgesToDelete.length > 0) {
            setEdges(currentEdges =>
              currentEdges.filter(edge => 
                !(nodesToDelete.has(edge.source) || 
                  (nodesToDelete.has(edge.target) && !hasOtherParents(edge.target, edge.source)))
              )
            );
          }
        }
      }
      
      // Apply all node changes
      onNodesChange(changes);
    },
    [nodes, edges, onNodesChange, setEdges]
  );

  const rfStyle = {
    backgroundColor: "#ffffff",
  };
  return (
    <div className="flex flex-row min-h-screen lg:flex-row">
      <div className="flex-grow h-screen" ref={reactFlowWrapper}>
        <div style={{marginLeft: '192px', flexGrow: 1, height: '100%'}}>
          <ReactFlow
            nodes={nodes}
            nodeTypes={nodeTypes}
            edges={edges}
            onNodesChange={handleNodesChange}
            onEdgesChange={handleEdgesChange}
            onConnect={onConnect}
            onInit={setReactFlowInstance}
            onDrop={onDrop}
            onDragOver={onDragOver}
            style={rfStyle}
            onNodeClick={onNodeClick}
            onPaneClick={() => {
              setSelectedElements([]); // Reset selected elements when clicking on pane
              setNodes((nodes) =>
                nodes.map((n) => ({
                  ...n,
                  selected: false, // Reset selected state of nodes when clicking on pane
                }))
              );
            }}
            defaultViewport={{
              x: (window.innerWidth / 3) - 100,
              y: 0,
              zoom: 0.8,
            }}
            fitView
            proOptions={{ hideAttribution: true }}
          >
            <Background variant="dots" gap={12} size={1}/>
            <Controls/>
            <MiniMap zoomable pannable style={{marginRight: '10px'}}/>
            <div
              className="transition-opacity duration-300"
              style={{opacity: isPanelVisible ? '1' : '0'}}
              onMouseEnter={() => setIsPanelVisible(true)}
              onMouseLeave={() => setIsPanelVisible(true)}
            >
              <Panel>
                <button
                  className="bg-gray-200 hover:bg-gray-400 text-xs text-grey-200 font-thin py-2 px-2 rounded"
                  onClick={onSave}
                >
                  {t('save')}
                </button>
                <button
                  className="hidden bg-gray-100 hover:bg-gray-400 text-xs text-grey-200  font-thin py-2 px-2 rounded"
                  onClick={onRestore}
                >
                  restore flow
                </button>
              </Panel></div>
          </ReactFlow>
        </div>
      </div>
      <LeftSidebar 
        prompts={prompts} 
        nodes={nodes} 
        toggleMuteAllPrompts={toggleMuteAllPrompts}
      />
      <Sidebar
        selectedNode={selectedElements[0]}
        setSelectedElements={setSelectedElements}
        nodeText={nodeText}
        setNodeText={setNodeText}
        nodePrompt={nodePrompt}
        setNodePrompt={setNodePrompt}
        nodePromptInitialMessage={nodePromptInitialMessage}
        setNodePromptInitialMessage={setNodePromptInitialMessage}
        nodePromptSystemInstruction={nodePromptSystemInstruction}
        setNodePromptSystemInstruction={setNodePromptSystemInstruction}
        nodeTool={nodeTool}
        setNodeTool={setNodeTool}
        nodeToolDescription={nodeToolDescription}
        setNodeToolDescription={setNodeToolDescription}
        nodeIntent={nodeIntent}
        setNodeIntent={setNodeIntent}
        nodeIntentDescription={nodeIntentDescription}
        setNodeIntentDescription={setNodeIntentDescription}
        nodeIntentSpeak={nodeIntentSpeak}
        setNodeIntentSpeak={setNodeIntentSpeak}
        nodeIntentForwardPhone={nodeIntentForwardPhone}
        setNodeIntentForwardPhone={setNodeIntentForwardPhone}
        prompts={prompts}
        nodeLinkto={nodeLinkto}
        setNodeLinkto={setNodeLinkto}
        nodeAgentName={nodeAgentName}
        setNodeAgentName={setNodeAgentName}
        nodeMuteUser={nodeMuteUser}
        setNodeMuteUser={setNodeMuteUser}
        edges={edges}
        nodes={nodes}
      />
    </div>
  );
};

// Wrap App with ReactFlowProvider
function App() {
  // Initialize global input protection on mount
  useEffect(() => {
    const protection = initGlobalInputProtection({
      enabled: true,
      showNotifications: true,
      notificationDuration: 3000,
      devWarnings: process.env.NODE_ENV === 'development',
    });

    // Cleanup on unmount
    return () => {
      if (protection) {
        protection.destroy();
      }
    };
  }, []);

  return (
    <GlobalStateProvider>
      <LanguageProvider>
        <BrowserRouter>
          <ReactFlowProvider>
            <FlowWithProvider/>
          </ReactFlowProvider>
        </BrowserRouter>
      </LanguageProvider>
    </GlobalStateProvider>
  );
}

export default App;
